import React, { FC, useCallback, useEffect, useState } from "react";

import { type Place, fetchDetails, fetchPlaces } from "@xapp/components";

import {
    FindAndConfirmProfileStep,
    FindAndConfirmProfileStepProps,
} from "components/Onboarding/components/V3/FindAndConfirmProfileStep";
import { getTemplateFromPlace } from "components/Onboarding/utils/getTemplateFromPlace";

import { OnboardingProps } from "../../models";

export interface FindAndConfirmProfileStepContainerProps
    extends Pick<FindAndConfirmProfileStepProps, "aside" | "style" | "className" | "requirePlace"> {
    initialProps: OnboardingProps;

    onNext(props: OnboardingProps): void;
    onBack?(): void;
}

function getSearchTerm(name: string, searchAddress: string): string {
    let search = `${name}`;
    if (searchAddress) {
        search += ` ${searchAddress}`;
    }
    return search;
}

export const FindAndConfirmProfileStepContainer: FC<FindAndConfirmProfileStepContainerProps> = (props) => {
    const { initialProps, onNext, onBack, ...rest } = props;

    const { place, name, searchAddress } = initialProps;

    const query = getSearchTerm(name, searchAddress);

    const [searchTerm, setSearchTerm] = useState<string>(query);
    const [placeToConfirm, setPlaceToConfirm] = useState<Place>(place);
    const [placeResults, setPlaceResults] = useState<Place[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    // const [error, setError] = useState<string | null>(null);

    /*
    useEffect(() => {
        ReactGA.event("onboarding_step_view", {
            step: "find_confirm_profile",
        });
    }, []);
    */

    useEffect(() => {
        // if a place now all of a sudden has operational status, we got more details about it
        if (query !== searchTerm) {
            setSearchTerm(query);
        }
    }, [query, searchTerm]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);

            // If we already have a Place ID (e.g. seeded from prospector, or one the user
            // previously selected), trust it and fetch its details directly. This skips the
            // fuzzy name+address text search, which can return several nearby candidates and
            // force the user to disambiguate a location we already know.
            if (place?.placeId) {
                const detailedPlace = await fetchDetails(place.placeId);
                if (detailedPlace) {
                    setPlaceToConfirm(detailedPlace);
                    setLoading(false);
                    return;
                }
                // fetchDetails resolves to undefined on a stale/invalid placeId; fall through to
                // the fuzzy text search so the user still gets candidates instead of a dead end.
            }

            const abortController = new AbortController();
            const { signal } = abortController;

            const places = await fetchPlaces(searchTerm, signal);

            // if we only have 1 result, we trust it and move on to the next step (confirm_profile)
            // if we have more than 1 result, we let the user choose the correct one
            if (places.length === 1) {
                const topPlace = places[0];
                // only one place, wait for the details to be fetched
                const detailedPlace = await fetchDetails(topPlace.placeId);
                setPlaceToConfirm(detailedPlace);
            } else {
                setPlaceResults(places);
            }
            setLoading(false);
        }

        fetchData();
    }, [searchAddress, name, place, searchTerm]);

    const handleSelect = useCallback((place: Place) => {
        async function fetchData() {
            if (place) {
                const detailedPlace = await fetchDetails(place.placeId);
                setPlaceToConfirm(detailedPlace);
            } else {
                setPlaceToConfirm(undefined);
            }
        }

        fetchData();
    }, []);

    const handleNext = useCallback(
        (confirmedPlace?: Place) => {
            const newProps: OnboardingProps = {
                ...initialProps,
                place: confirmedPlace,
            };

            // if we have a website, add it
            if (confirmedPlace && confirmedPlace.website) {
                newProps.website = confirmedPlace.website;
            }

            if (confirmedPlace && confirmedPlace.types) {
                const newTemplate = getTemplateFromPlace(confirmedPlace);
                if (newTemplate) {
                    newProps.template = {
                        ...newTemplate,
                        // keep the original label they entered
                        name: initialProps.template?.name || newTemplate.name,
                    };
                }
            }

            onNext(newProps);
        },
        [initialProps, onNext]
    );

    return (
        <FindAndConfirmProfileStep
            {...rest}
            place={placeToConfirm}
            results={placeResults}
            loading={loading}
            searchTerms={searchTerm}
            onSelect={handleSelect}
            onNext={handleNext}
            onBack={onBack}
        />
    );
};
